// ==================== SMS ANALYTICS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageSquare, Send, CheckCircle2, XCircle, Clock, ArrowRight, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { staggerItem } from '../../../utils/animations';
import type { SmsAnalyticsData } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface SmsAnalyticsProps {
  data: SmsAnalyticsData | null;
  isLoading?: boolean;
}

// ==================== MINI CHART ====================

const SmsSparkline = ({ data }: { data: Array<{ date: string; sent: number; delivered: number; failed: number }> }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.sent + d.delivered + d.failed), 1);
  const chartHeight = 40; // Fixed height in pixels
  
  return (
    <div className="flex items-end gap-1" style={{ height: `${chartHeight}px` }}>
      {data.slice(-7).map((day) => {
        const total = day.sent + day.delivered + day.failed;
        const sentHeight = Math.max((day.sent / maxCount) * chartHeight, day.sent > 0 ? 4 : 0);
        const deliveredHeight = Math.max((day.delivered / maxCount) * chartHeight, day.delivered > 0 ? 4 : 0);
        const failedHeight = Math.max((day.failed / maxCount) * chartHeight, day.failed > 0 ? 4 : 0);
        
        return (
          <div
            key={day.date}
            className="flex-1 min-w-[16px] flex flex-col justify-end gap-px cursor-pointer group relative"
            style={{ height: `${chartHeight}px` }}
          >
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
              <div className="bg-(--color-midnight-navy) border border-(--color-border-gray) rounded px-2 py-1 text-[10px] whitespace-nowrap shadow-lg">
                <p className="text-(--color-muted-gray)">{new Date(day.date).toLocaleDateString()}</p>
                <p className="text-blue-400">Sent: {day.sent}</p>
                <p className="text-green-400">Delivered: {day.delivered}</p>
                <p className="text-red-400">Failed: {day.failed}</p>
              </div>
            </div>
            
            {/* Stacked bars - from bottom: sent, delivered, failed */}
            {total > 0 ? (
              <>
                {day.failed > 0 && (
                  <div
                    className="w-full bg-red-500 rounded-t"
                    style={{ height: `${failedHeight}px` }}
                  />
                )}
                {day.delivered > 0 && (
                  <div
                    className="w-full bg-green-500"
                    style={{ height: `${deliveredHeight}px` }}
                  />
                )}
                {day.sent > 0 && (
                  <div
                    className="w-full bg-blue-500"
                    style={{ height: `${sentHeight}px` }}
                  />
                )}
              </>
            ) : (
              <div className="w-full bg-(--color-border-gray) rounded-t" style={{ height: '4px' }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==================== COMPONENT ====================

export const SmsAnalytics = ({ data, isLoading = false }: SmsAnalyticsProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">SMS Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 animate-pulse">
              <div className="h-10 bg-(--color-border-gray) rounded" />
              <div className="h-16 bg-(--color-border-gray) rounded" />
              <div className="h-10 bg-(--color-border-gray) rounded" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const totalMessages = (data?.totals.sent || 0) + (data?.totals.pending || 0);
  const hasData = data && totalMessages > 0;

  if (!hasData) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">SMS Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="py-4 text-center">
              <MessageSquare size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
              <p className="text-xs text-(--color-muted-gray)">No SMS messages yet</p>
              <Link to="/app/sms-messages" className="text-xs text-(--color-signal-orange) hover:underline mt-1 inline-block">
                Send SMS
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-1.5">
              <MessageSquare size={14} className="text-purple-400" />
              SMS Quick Stats
            </CardTitle>
            <Link 
              to="/app/sms-messages" 
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:underline"
            >
              View All
              <ArrowRight size={10} />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-2">
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="flex items-center justify-center gap-1">
                  <Send size={10} className="text-blue-400" />
                  <p className="text-lg font-bold text-white">{data.totals.sent}</p>
                </div>
                <p className="text-[10px] text-(--color-muted-gray)">Sent</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 size={10} className="text-green-400" />
                  <p className="text-lg font-bold text-green-400">{data.totals.delivered}</p>
                </div>
                <p className="text-[10px] text-(--color-muted-gray)">Delivered</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="flex items-center justify-center gap-1">
                  <XCircle size={10} className="text-red-400" />
                  <p className="text-lg font-bold text-red-400">{data.totals.failed}</p>
                </div>
                <p className="text-[10px] text-(--color-muted-gray)">Failed</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="flex items-center justify-center gap-1">
                  <Clock size={10} className="text-yellow-400" />
                  <p className="text-lg font-bold text-yellow-400">{data.totals.pending}</p>
                </div>
                <p className="text-[10px] text-(--color-muted-gray)">Pending</p>
              </div>
            </div>

            {/* Delivery Rate */}
            <div className="p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-(--color-muted-gray)">Delivery Rate</p>
                <p className="text-sm font-semibold text-green-400">{data.totals.deliveryRate}%</p>
              </div>
              <div className="h-1.5 bg-(--color-charcoal-black) rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${data.totals.deliveryRate}%` }}
                />
              </div>
            </div>

            {/* Sparkline */}
            {data.dailyStats.length > 0 && (
              <div className="p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <p className="text-[10px] text-(--color-muted-gray) mb-1">Message Trend (7 days)</p>
                <SmsSparkline data={data.dailyStats} />
                <div className="flex items-center justify-center gap-3 mt-1">
                  <div className="flex items-center gap-1 text-[9px] text-(--color-muted-gray)">
                    <div className="w-2 h-2 bg-blue-500 rounded-sm" /> Sent
                  </div>
                  <div className="flex items-center gap-1 text-[9px] text-(--color-muted-gray)">
                    <div className="w-2 h-2 bg-green-500 rounded-sm" /> Delivered
                  </div>
                  <div className="flex items-center gap-1 text-[9px] text-(--color-muted-gray)">
                    <div className="w-2 h-2 bg-red-500 rounded-sm" /> Failed
                  </div>
                </div>
              </div>
            )}

            {/* Direction Stats */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <ArrowUpRight size={14} className="text-blue-400" />
                <div>
                  <p className="text-sm font-semibold text-white">{data.byDirection.outbound}</p>
                  <p className="text-[10px] text-(--color-muted-gray)">Outbound</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <ArrowDownLeft size={14} className="text-green-400" />
                <div>
                  <p className="text-sm font-semibold text-white">{data.byDirection.inbound}</p>
                  <p className="text-[10px] text-(--color-muted-gray)">Inbound</p>
                </div>
              </div>
            </div>

            {/* Segment Stats */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <div>
                <p className="text-[10px] text-(--color-muted-gray)">Total Segments</p>
                <p className="text-sm font-semibold text-white">{data.totalSegments}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-(--color-muted-gray)">Avg per Message</p>
                <p className="text-sm font-semibold text-purple-400">{data.avgSegmentsPerMessage}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SmsAnalytics;
